import { useEffect, useState } from "react";

import { pressRelease } from "../../../services/api/PressRelease";
import { newsFetch } from "../../../services/api/News";

import PageHeader from "../../Header";

import PressReleaseCard, { PressReleaseItem } from "./PressReleaseCard";

import Pagination from "./Pagination";

import NewsCard, { NewsItem } from "../blogssection/NewsCard";

import PaginationBlogs from "../blogssection/Pagination";

interface Meta {
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
  from?: number;
  to?: number;
}

interface ApiResponse {
  status: number;
  ok: boolean;
  data: {
    data: NewsItem[];
    success: boolean;
    status: number;
    meta: Meta;
  };
}

const PressReleasesPage: React.FC = () => {
  // =========================
  // PRESS RELEASE STATES
  // =========================
  const [press, setPress] = useState<PressReleaseItem[]>([]);
  const [pressMeta, setPressMeta] = useState<Meta | null>(null);
  const [pressPage, setPressPage] = useState(1);

  // =========================
  // NEWS STATES
  // =========================
  const [news, setNews] = useState<NewsItem[]>([]);
  const [newsMeta, setNewsMeta] = useState<Meta | null>(null);
  const [newsPage, setNewsPage] = useState(1);

  // =========================
  // COMMON STATES
  // =========================
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // =========================
  // FETCH PRESS + NEWS
  // =========================
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError("");

        // Fetch both APIs together
        const [pressResponse, newsResponse] = await Promise.all([
          pressRelease(`press-releases?page=${pressPage}`),
          newsFetch(`news?page=${newsPage}`),
        ]);

        // =========================
        // PRESS RELEASE RESPONSE
        // =========================
        setPress(pressResponse?.data?.data || []);
        setPressMeta(pressResponse?.data?.meta || null);

        // =========================
        // NEWS RESPONSE
        // =========================
        const newsApiResponse: ApiResponse = newsResponse;

        if (newsApiResponse.ok && newsApiResponse.data?.success) {
          setNews(newsApiResponse.data.data || []);
          setNewsMeta(newsApiResponse.data.meta || null);
        } else {
          setNews([]);
          setNewsMeta(null);
        }
      } catch (err) {
        console.error("Error fetching data:", err);

        setError("Failed to load data.");

        setPress([]);
        setPressMeta(null);

        setNews([]);
        setNewsMeta(null);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [pressPage, newsPage]);

  // =========================
  // PRESS PAGINATION
  // =========================
  const handlePressPageChange = (newPage: number) => {
    setPressPage(newPage);

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  // =========================
  // NEWS PAGINATION
  // =========================
  const handleNewsPageChange = (newPage: number) => {
    setNewsPage(newPage);

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  return (
    <div className="min-h-screen bg-[#f7f7f7]">
      {/* HEADER */}
      <PageHeader
        title="News & Blogs"
        paragraph="Stay updated with the latest news, blogs, insights, stories, and updates."
        backgroundImage=""
        align="center"
      />

      {/* CONTENT */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14">
        {/* =========================
            LOADING
        ========================= */}
        {loading && (
          <div className="flex justify-center py-20">
            <div className="w-10 h-10 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin" />
          </div>
        )}

        {/* =========================
            ERROR
        ========================= */}
        {!loading && error && (
          <div className="bg-red-50 border border-red-200 text-red-500 rounded-2xl p-6 text-center">
            {error}
          </div>
        )}

        {!loading && !error && (
          <>
            {/* =========================
                PRESS RELEASES
            ========================= */}
            <section className="mb-16">
              <div className="flex items-center justify-between mb-8">
                <h2 className="text-3xl font-bold text-gray-900">News</h2>
              </div>

              {press.length === 0 ? (
                <div className="bg-gray-50 border border-gray-200 rounded-2xl p-10 text-center text-gray-500">
                  No News Releases Available
                </div>
              ) : (
                <>
                  <div
                    className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 md:gap-8
 items-stretch"
                  >
                    {press.map((item) => (
                      <PressReleaseCard key={item.id} item={item} />
                    ))}
                  </div>

                  {pressMeta && (
                    <Pagination
                      meta={pressMeta}
                      page={pressPage}
                      onPageChange={handlePressPageChange}
                    />
                  )}
                </>
              )}
            </section>

            {/* =========================
                NEWS / BLOGS
            ========================= */}
            <section>
              <div className="flex items-center justify-between mb-8">
                <h2 className="text-3xl font-bold text-gray-900">Blogs</h2>
              </div>

              {news.length === 0 ? (
                <div className="bg-gray-50 border border-gray-200 rounded-2xl p-10 text-center text-gray-500">
                  No Blogs Available
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
                    {news.map((item) => (
                      <NewsCard key={item.id} item={item} />
                    ))}
                  </div>

                  {newsMeta && (
                    <PaginationBlogs
                      currentPage={newsPage}
                      totalPages={newsMeta.last_page}
                      totalItems={newsMeta.total}
                      onPageChange={handleNewsPageChange}
                    />
                  )}
                </>
              )}
            </section>
          </>
        )}
      </div>
    </div>
  );
};

export default PressReleasesPage;
